import Big from 'big.js';
import _ from 'lodash';
import { DateTime } from 'luxon';

import { TypePolicies } from '@apollo/client';
import * as graphql from 'generated/graphql';

import {
  currencyAndDecimalFieldPolicy,
  dateTimeFieldPolicy
} from './fieldPolicies';

import { AccountDataPoint } from './accountDataPoint';
import { Calculation } from './calculation';
import { CalculationInput } from './calculationInput';
import { CompanyStatistics } from './companyStatistics';
import { DeepDiveTopic } from './deepDiveTopic';
import { EarningsCallSummary } from './earningsSummary';
import { EconomicIndex } from './economicIndex';
import { GlAccount } from './glAccount';
import { Invoice } from './invoice';
import { ListedCompany } from './listedCompany';
import { MessageData } from './messageData';
import { MessageDataValue } from './messageDataValue';
import { Person } from './person';
import { Price } from './price';
import { PriceTier } from './priceTier';
import { Query } from './query';
import {
  ITypeSpecificPolicy,
  formatLargeValue
} from './shared';
import { SubscriptionPlan } from './subscriptionPlan';



const ApiFile: ITypeSpecificPolicy<graphql.ApiFile> = {
  fields: {
    createdTimestamp: dateTimeFieldPolicy(),
    urlExpiry: dateTimeFieldPolicy()
  }
};



const BreakdownValue: ITypeSpecificPolicy<graphql.BreakdownValue> = {
  fields: {
    value: currencyAndDecimalFieldPolicy(),
    formattedValue: {
      read: (existing, { readField }) => {
        const value = readField<Big>('value');

        if (!_.isNil(value)) {
          return formatLargeValue(value);
        }
        return existing;
      }
    }
  }
};



const DataMetric: ITypeSpecificPolicy<graphql.DataMetric> = {
  fields: {
    value: currencyAndDecimalFieldPolicy(),
    changeMonthOverMonthPercent: currencyAndDecimalFieldPolicy()
  }
};



const HistoricalDataPoint: ITypeSpecificPolicy<graphql.HistoricalDataPoint> = {
  fields: {
    date: dateTimeFieldPolicy(),
    value: currencyAndDecimalFieldPolicy()
  }
};



const Message: ITypeSpecificPolicy<graphql.Message> = {
  fields: {
    createdTimestamp: dateTimeFieldPolicy(),

    isHistorical: {
      read: (existing, { readField }) => {
        const val = readField<DateTime>('createdTimestamp');
        if (!val) {
          return existing;
        }

        return Math.abs(val.diffNow().as('days')) > 2;
      }
    }
  }
};

const SiteUserFull: ITypeSpecificPolicy<graphql.SiteUserFull> = {
  fields: {
    createdTimestamp: dateTimeFieldPolicy()
  }
};

const Thread: ITypeSpecificPolicy<graphql.Thread> = {
  fields: {
    createdTimestamp: dateTimeFieldPolicy()
  }
};

const TrendDirection: ITypeSpecificPolicy<graphql.TrendDirection> = {
  fields: {
    calculatedValue: currencyAndDecimalFieldPolicy()
  }
};

const StockPriceDataPoint: ITypeSpecificPolicy<graphql.StockPriceDataPoint> = {
  fields: {
    date: dateTimeFieldPolicy(),
    value: currencyAndDecimalFieldPolicy(),
    fiftyTwoWeekLow: currencyAndDecimalFieldPolicy(),
    fiftyTwoWeekHigh: currencyAndDecimalFieldPolicy(),
    volume: currencyAndDecimalFieldPolicy(),

    formattedVolume: {
      read: (existing, { readField }) => {
        const volume = readField<Big>('volume');

        if (!_.isNil(volume)) {
          return volume.toNumber().toLocaleString('en-US', {
            useGrouping: true,
            maximumFractionDigits: 0
          });
        }
        return existing;
      }
    }
  }
};



export const typePolicies: TypePolicies = {
  AccountDataPoint,
  ApiFile,
  BreakdownValue,
  Calculation,
  CalculationInput,
  CompanyStatistics,
  DataMetric,
  DeepDiveTopic,
  EarningsCallSummary,
  EconomicIndex,
  GlAccount,
  HistoricalDataPoint,
  Invoice,
  ListedCompany,
  Message,
  MessageData,
  MessageDataValue,
  Person,
  Price,
  PriceTier,
  Query,
  SiteUserFull,
  StockPriceDataPoint,
  SubscriptionPlan,
  Thread,
  TrendDirection
};
